import React, { useState } from 'react';
import { Bell, CheckCircle2, Smartphone, AlertCircle, Loader2, Send } from 'lucide-react';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { atlasBackendFetch } from '../../lib/atlasBackend';
import { PreferenciaWhatsApp } from './PreferenciaWhatsApp';

interface NotificacionesProps {
  profile: any;
  /** dev_tester vive en company_profiles; Settings lo baja aparte y lo pasa acá (el `profile`
   *  de Settings NO trae dev_tester). Habilita el botón "Enviar prueba". */
  devTester?: boolean;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

/**
 * Sección "Notificaciones" de Configuración: opt-in de push (spec 2026-08-25) y el interruptor
 * de WhatsApp (ADR-2026-11-02-interruptor-de-whatsapp).
 * Aditivo: no toca otras secciones. La lógica vive en hooks/usePushNotifications + lib/push.
 */
export const Notificaciones: React.FC<NotificacionesProps> = ({ devTester, setError, setSuccess }) => {
  const { soporte, permiso, suscripto, cargando, activar, desactivar } = usePushNotifications();
  const [trabajando, setTrabajando] = useState(false);
  const [enviandoPrueba, setEnviandoPrueba] = useState(false);

  const esDevTester = devTester === true;

  const onActivar = async () => {
    setTrabajando(true);
    setError(null);
    setSuccess(null);
    const r = await activar();
    setTrabajando(false);
    if (r.ok) {
      setSuccess('¡Listo! Vas a recibir notificaciones de Atlas en este dispositivo.');
    } else if (r.motivo === 'permiso-denegado') {
      setError('No diste permiso. Activá las notificaciones para Atlas en tu navegador y probá de nuevo.');
    } else {
      setError('No se pudieron activar las notificaciones. Probá de nuevo.');
    }
  };

  const onDesactivar = async () => {
    setTrabajando(true);
    setError(null);
    setSuccess(null);
    await desactivar();
    setTrabajando(false);
    setSuccess('Notificaciones desactivadas en este dispositivo.');
  };

  const onPrueba = async () => {
    setEnviandoPrueba(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await atlasBackendFetch('/v1/push/test', {});
      if (res.ok) {
        const data = (await res.json().catch(() => ({}))) as { enviadas?: number };
        setSuccess(`Notificación de prueba enviada a ${data.enviadas ?? 0} dispositivo(s). Debería llegarte en unos segundos.`);
      } else {
        setError('No se pudo enviar la prueba.');
      }
    } catch {
      setError('No se pudo enviar la prueba.');
    }
    setEnviandoPrueba(false);
  };

  const Header = (
    <div className="mb-5">
      <h2 className="text-[17px] font-semibold text-brand-black/90 flex items-center gap-2">
        <Bell className="w-5 h-5 text-brand-primary" /> Notificaciones
      </h2>
      <p className="text-[13px] text-brand-black/50 mt-1">
        Recibí avisos de Atlas para no perderte nada.
      </p>
    </div>
  );

  const contenidoPush = (): React.ReactNode => {
    if (cargando) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
        </div>
      );
    }

    // El navegador no soporta push, o iOS sin la PWA instalada.
    if (soporte !== 'ok') {
      const iosNoInstalada = soporte === 'ios-no-instalada';
      return (
        <div className="rounded-lg bg-brand-primary/[0.04] border border-brand-primary/15 p-4 flex items-start gap-3">
          {iosNoInstalada ? (
            <Smartphone className="w-5 h-5 text-brand-primary shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-brand-black/40 shrink-0 mt-0.5" />
          )}
          <div className="text-[13px] text-brand-black/70">
            {iosNoInstalada ? (
              <>
                Para recibir notificaciones en tu iPhone necesitás <strong>instalar Atlas</strong> en tu
                pantalla de inicio (compartir → "Agregar a inicio") y abrirlo desde ahí.
              </>
            ) : (
              <>Tu navegador no admite notificaciones. Probá desde Chrome, Edge o instalando la app.</>
            )}
          </div>
        </div>
      );
    }

    // Permiso bloqueado a nivel navegador → no se puede re-pedir por código.
    if (permiso === 'denied') {
      return (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-[13px] text-brand-black/70">
            Bloqueaste las notificaciones para Atlas. Activalas desde los ajustes de tu navegador
            (candado junto a la dirección → Notificaciones → Permitir) y volvé a entrar.
          </div>
        </div>
      );
    }

    return suscripto ? (
      <div className="space-y-4">
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
          <div className="text-[14px] font-medium text-brand-black/80">
            Notificaciones activadas en este dispositivo
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onDesactivar}
            disabled={trabajando}
            className="h-10 px-4 rounded-md border border-brand-black/15 text-brand-black/70 text-[14px] font-medium hover:bg-brand-black/[0.03] disabled:opacity-70 transition-colors"
          >
            {trabajando ? 'Desactivando…' : 'Desactivar'}
          </button>
          {esDevTester && (
            <button
              onClick={onPrueba}
              disabled={enviandoPrueba}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-70 text-white text-[14px] font-medium shadow-sm transition-colors"
            >
              <Send className="w-4 h-4" />
              {enviandoPrueba ? 'Enviando…' : 'Enviar prueba'}
            </button>
          )}
        </div>
      </div>
    ) : (
      <div className="space-y-4">
        <p className="text-[14px] text-brand-black/70">
          Activá las notificaciones para que te avisemos cuando valga la pena volver a crear contenido.
        </p>
        <button
          onClick={onActivar}
          disabled={trabajando}
          className="inline-flex items-center gap-2 h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-70 text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
        >
          <Bell className="w-4 h-4" />
          {trabajando ? 'Activando…' : 'Activar notificaciones'}
        </button>
      </div>
    );
  };

  return (
    <div>
      {Header}
      {contenidoPush()}
      {/* WhatsApp no depende del navegador: se muestra siempre, aunque el push no se pueda. */}
      <PreferenciaWhatsApp setError={setError} setSuccess={setSuccess} />
    </div>
  );
};
